import {
    mergeTranslations,
    TranslationMessages as BaseTranslationMessages,
} from 'react-admin';
import englishMessages from 'ra-language-english';
import { RaTreeTranslationMessages } from '@react-admin/ra-tree';

export interface TranslationMessages
    extends RaTreeTranslationMessages, BaseTranslationMessages {}

const customEnglishMessages: TranslationMessages = mergeTranslations(
    englishMessages,
    {
        'ra-form-layout': {
            action: {
                next: 'Next',
            },
        },
        'ra-tree': {
            action: {
                add_root: 'Add a category of products',
            },
        },
        'ra-search': {
            result: `1 result |||| %{smart_count} results`,
        },
        'ra-realtime': {
            notification: {
                lock: {
                    lockedByMe: 'Locked for you',
                    lockedBySomeoneElse: 'Locked by someone else',
                    unlocked: 'Lock released',
                },
            },
        },
        'ra-preferences': {
            action: {
                choose_columns: 'Choose columns',
                toggle_theme: 'Toggle theme',
            },
        },
        ra: {
            action: {
                select_columns: 'Choose columns',
                toggle_theme: 'Toggle theme',
                copy: 'Copy to clipboard',
            },
            page: {
                empty: 'No results found',
            },
            saved_queries: {
                label: 'Saved',
                query_name: 'Query name',
                new_label: 'Save current query',
                new_dialog_title: 'Save current query as',
                remove_label: 'Remove saved query',
                remove_label_with_name: 'Remove query "%{name}"',
                remove_dialog_title: 'Remove saved query?',
                remove_message: 'Are you sure you want to remove the query?',
                help: 'First filter the list and save the query for future use.',
            },
        },
        actions: {
            export: 'Export to Excel',
            login_as: {
                column_label: 'Preview',
                teacher_tooltip: 'Log in as a teacher',
                student_tooltip: 'Log in as a student',
                pupil_tooltip: 'Log in as a pupil',
                user_inactive: 'User is not active',
                profile_locked: 'Profile is locked',
                login_not_allowed: 'Login is not allowed',
                access_starts_on: 'Access starts on %{date}',
                access_expired_on: 'Access expired on %{date}',
            },
            copy_access_token: {
                column_label: 'Extra action',
                tooltip: 'Copy access_token to clipboard (observer)',
                copied: 'Access_token copied to clipboard',
                user_inactive: 'User is not active',
            },
        },
        notifications: {
            SAVE_SUCCESS: 'Saved',
            MPASS_REMOVED: 'MPASS identifier removed',
            ACTION_ERROR: 'Action failed',
            SESSION_REMOVED: 'Session removed and user logged out.',
            KV_USER_NOT_FOUND:
                'User not found in Kirjavälitys with this identifier',
            KV_EMAIL_ALREADY_USED: 'Email is already in use in Storia',
            NETWORK_ERRROR: 'Network error',
            EMAIL_ALREADY_USED: 'Email is already in use',
            EXPORT_ERROR: 'Error saving file',
        },
        menu: {
            audience: 'Users',
            sales: 'Test',
            catalog: 'Catalog',
            customers: 'Customers',
            hallinta: 'Administration',
        },
        hallinta: {
            tabs: {
                aws_overview: 'AWS Overview',
                secrets: 'Secrets Manager',
                jwt_keys: 'JWT Keys',
                oauth_clients: 'OAuth Clients',
                api_configs: 'API Configs',
                settings: 'Settings',
            },
            tests: {
                fields: {
                    add_learners_to_groups:
                        'Add pupils and students to the group',
                    add_learners_to_groups_help:
                        'When off, pupils and students are linked to the school but not to the test group.',
                    mpass_profile: 'Create users with an MPASS profile',
                    mpass_profile_help:
                        'Adds role-appropriate MPASS sign-in data with a generated OID. The test school code is used as-is.',
                },
            },
            settings: {
                campaign_csv_email: 'Default campaign CSV recipient',
                title: 'List view settings',
                turnstile_title: 'Nova Turnstile security check',
                turnstile_enabled: 'Turnstile verification enabled for login',
                turnstile_enabled_help:
                    'Enables or disables Turnstile verification for teacher, pupil, and student Nova login in this environment. Does not affect student registration verification. Disabling it in production reduces bot protection.',
                turnstile_disable_confirm_title:
                    'Disable Turnstile for login?',
                turnstile_disable_confirm_content:
                    'Turnstile verification will be disabled for Nova logins in this environment. Student registration verification remains enabled. This reduces login bot protection.',
                turnstile_dev_scenario: 'Login test scenario in Dev',
                turnstile_dev_warning:
                    'The test scenario affects all Nova logins in Dev, not student registration. Restore Normal after testing.',
                turnstile_credentials_help:
                    'AWS keys are used for Nova login and student registration. Edit them in a separate dialog. Keys are loaded from AWS Secrets Manager at startup, so restart the services that use them after changing keys.',
                turnstile_keys_edit: 'Edit AWS keys',
                turnstile_keys_title: 'Turnstile keys in AWS Secrets Manager',
                turnstile_keys_current_env: 'Keys can only be edited on the current server environment tab.',
                turnstile_keys_help: 'Only modified roles are saved. Other AWS secret fields are preserved. Only the final four characters of secret keys are shown; short keys are fully masked.',
                turnstile_keys_current_secret: 'Current secret key',
                turnstile_keys_restart: 'Saving updates the AWS secret, not running services. Restart the OIDC and registration services. If the student site key changes, also update NEXT_PUBLIC_TURNSTILE_SITE_KEY in the registration frontend.',
                turnstile_keys_keep_secret: 'A key is configured. Leave blank to keep the current secret key.',
                turnstile_keys_missing_secret: 'No secret key is configured.',
                turnstile_keys_pair_required: 'Provide both the site key and its matching secret key when adding or changing a site key. This form cannot delete keys.',
                turnstile_keys_saved: 'Keys saved to AWS. Restart the services that use them.',
                turnstile_keys_error: 'Could not read or save AWS keys. Check permissions and the AWS secret structure.',
                turnstile_keys_conflict: 'The AWS secret has changed. Close and reopen the dialog before saving.',
                turnstile_role_teacher: 'Teacher',
                turnstile_role_pupil: 'Pupil',
                turnstile_role_student: 'Student',
                turnstile_scenario_normal: 'Normal — use the Dev application keys',
                turnstile_scenario_always_pass:
                    'Cloudflare test keys — widget and validation succeed',
                turnstile_scenario_always_fail:
                    'Cloudflare test keys — widget and validation fail',
                turnstile_scenario_server_failure:
                    'Cloudflare test keys — server-side validation fails',
                turnstile_scenario_duplicate_token:
                    'Cloudflare test keys — token already spent',
                turnstile_scenario_interactive:
                    'Cloudflare test keys — interactive challenge',
                turnstile_scenario_client_error:
                    'Simulate a browser widget error',
                turnstile_scenario_token_expired:
                    'Simulate an expired token',
                turnstile_scenario_pending:
                    'Simulate a stuck verification',
                turnstile_scenario_missing_token:
                    'Simulate a missing token',
                turnstile_scenario_siteverify_unreachable:
                    'Simulate a Cloudflare verification network error',
                list_sessions: 'Fetch session data in list views',
                list_sessions_help:
                    'When off, user list views (teacher/pupil/student/user) do not fetch session data from Redis. Speeds up lists under load; the "online" indicator will not be shown.',
                user_data_recheck_title: 'User data recheck interval',
                user_data_recheck_help:
                    'How many days a user\'s or school relation\'s last-confirmed data stays "up-to-date" before user_data_state reports "stale". Default 248 days (~8 months). Allowed 1–3650.',
                user_data_recheck_days_invalid:
                    'Enter an integer between 1 and 3650',
                list_stale_info: 'Show data staleness in list views',
                list_stale_info_help:
                    'When on, teacher/student/pupil list and edit views compute and show user_data_state ("up-to-date"/"stale"). When off, it is not computed or returned.',
                security_title: 'Authentication',
                basic_login: 'Allow basic authentication (username/password)',
                basic_login_help:
                    'Deprecated — the primary login is AdLogin (Entra). When off, the backend rejects username/password login (403). Enable temporarily only if AdLogin is down. During a full AdLogin outage (you cannot log in here) use the environment variable BASIC_LOGIN_ENABLED=true.',
                save: 'Save',
                saved: 'Setting saved',
                error: 'Failed to save setting',
                invitation_ttl: 'Teacher invitation validity (days)',
                invitation_ttl_help:
                    'How many days an invitation is shown as valid (envelope state and activation link) and its delivery status is re-checked. Applies to all invitations, including existing ones. Allowed 1–365.',
                invitation_ttl_invalid: 'Enter an integer between 1 and 365',
                invitation_ttl_enforced: 'Block expired invitations (backend)',
                invitation_ttl_enforced_help:
                    'When on, the backend rejects activation and validation of expired invitations (created + validity days). Applies to all invitations, including existing ones. Enable only once the Nova frontend shows the validity period.',
                invitation_title: 'Invitation settings',
                auto_resend: 'Automatic resend',
                auto_resend_enabled: 'Automatic resend enabled',
                auto_resend_enabled_help:
                    'When on, invitations are resent automatically following the backoff schedule and the selected target. Off by default.',
                auto_resend_mode: 'Which invitations to resend',
                auto_resend_mode_all: 'All — reminder to everyone pending',
                auto_resend_mode_stuck_failed: 'Stuck / failed (not delivered)',
                auto_resend_schedule: 'Schedule (days, comma-separated)',
                auto_resend_schedule_help:
                    'e.g. 3, 10, 30 = 1st attempt 3 days after the last send, 2nd after 10 days, 3rd after 30 days. Number of entries = max attempts. 1–20 entries, each 1–365.',
                auto_resend_schedule_invalid:
                    'Enter 1–20 integers (each 1–365), comma-separated',
                license_cache_title: 'Kumppani API cache settings',
                license_cache_subtitle:
                    'License check (GET /license/user/{guid})',
                license_data_cache_enabled: 'Data cache enabled',
                license_data_cache_enabled_help:
                    'When off, the license check skips the new guid-keyed data cache entirely and always uses the original filtered SQL query (does not affect the TTL setting below). Safe rollback lever if the new mechanism causes problems in production.',
                license_cache_ttl: 'Cache TTL (s)',
                license_cache_ttl_help:
                    "How long the kumppani-API license check (/license/user/{guid}) keeps a user's full license list in the Valkey cache. Proactively refreshed on material click (prewarm); this is a safety-net upper bound. 0 = disable caching. Allowed 0–3600.",
                license_cache_ttl_invalid:
                    'Enter an integer between 0 and 3600',
                license_cache_prewarm: 'Prewarm enabled',
                license_cache_prewarm_help:
                    'When on, clicking a material (if it has the prewarm flag) proactively refreshes the license cache before the kumppani-API call arrives. Turn off only if prewarm causes extra load - the TTL-based cache keeps working regardless.',
                teacher_cache_subtitle: 'Teacher data (GET /teacher/{guid})',
                teacher_data_cache_enabled: 'Data cache enabled',
                teacher_data_cache_enabled_help:
                    'When off, fetching teacher data skips the Valkey data cache entirely (ttl forced to 0) and always queries the database directly. Safe rollback lever if the new mechanism causes problems in production.',
                teacher_cache_ttl: 'Cache TTL (s)',
                teacher_cache_ttl_help:
                    'How long the full teacher data structure (classes, pupils) is kept in the Valkey cache (teacher_full_data_{guid}). 0 = disable caching. Allowed 0–604800 (7 days).',
                teacher_cache_ttl_invalid:
                    'Enter an integer between 0 and 604800',
                teacher_cache_prewarm: 'Prewarm enabled',
                teacher_cache_prewarm_help:
                    'When on, clicking a material (if it has the prewarm flag) proactively refreshes the teacher data cache. Turn off only if prewarm causes extra load.',
                teacher_cache_invalidate_on:
                    'Group event cache updates enabled',
                teacher_cache_invalidate_on_help:
                    'When on, group/pupil/student changes (join, removal, TEACHER_UPDATED) automatically refresh the teacher cache in the background. Turn off e.g. during term/period changes if the volume of group events overloads the database - the cache will then only expire via TTL. Does not affect prewarm or the cache flush on teacher deletion.',
                product_mem_cache_subtitle:
                    'Product cache process memory (all_products/all_student_products/all_pupil_products)',
                product_mem_cache_broadcast_enabled:
                    'Background refresh enabled',
                product_mem_cache_broadcast_enabled_help:
                    'When on, every backend process refreshes its own in-memory product list automatically as soon as pg_cron rebuilds the Valkey cache (~every 10 min) - this prevents the process memory from expiring on its own (STALE) and hitting a random customer request instead. Turn off only if the broadcast causes issues - process memory still works via its own (longer, safety-net) TTL.',
                save_as_defaults: 'Save current as defaults',
                save_as_defaults_confirm_title: 'Save defaults?',
                save_as_defaults_confirm_content:
                    'The currently active settings will be saved as the restore point. Overwrites any previously saved defaults.',
                defaults_saved: 'Current settings saved as defaults',
                reset_to_defaults: 'Reset to defaults',
                reset_to_defaults_confirm_title: 'Reset settings to defaults?',
                reset_to_defaults_confirm_content:
                    'All active settings will be replaced with the last saved defaults. This cannot be undone.',
                reset_done: 'Settings reset to defaults',
                read_only_env_notice:
                    "Showing another environment's settings - read only. Editing is only possible from production, and takes effect in the target environment after the next nightly database restore.",
            },
            api_configs: {
                fields: {
                    application_name: 'Application',
                    key: 'API Key',
                    name: 'Name',
                    type: 'Type',
                    is_default: 'Default',
                    role: 'Role',
                    policy: 'Policy',
                    adminrights: 'Admin Rights',
                    allowed_swaggers: 'Allowed Swaggers',
                    userrights: 'User Rights (JSON)',
                    jwt_algorithm: 'Algorithm',
                    jwt_ttl: 'Token TTL',
                    oidc_code_ttl: 'OIDC Code TTL',
                    session_config: 'Session Config (JSON)',
                    login_config: 'Login Config (JSON)',
                    scope: 'Scope',
                    status: 'Status',
                },
                sections: {
                    basic: 'Basic Info',
                    jwt: 'JWT Settings',
                    json_config: 'Settings',
                    jwt_keys: 'Linked JWT Signing Keys',
                    oauth_clients: 'Linked OAuth Clients',
                },
                actions: {
                    back: 'API Configs',
                },
                no_jwt_keys: 'No JWT signing keys linked to this config.',
                no_oauth_clients: 'No OAuth clients linked to this config.',
            },
            oauth_clients: {
                fields: {
                    name: 'Name',
                    type: 'Type',
                    client_id: 'Client ID',
                    client_secret: 'Client Secret',
                    api_key: 'API Key',
                    env: 'Environment',
                    status: 'Status',
                    theme: 'Theme',
                    back_channel_logout_uri: 'Back-channel Logout URI',
                    applications: 'Applications',
                    created: 'Created',
                    modified: 'Modified',
                    guid: 'GUID',
                    basic_auth_badge: 'Basic Auth',
                },
                status: {
                    active: 'Active',
                    disabled: 'Disabled',
                },
                tabs: {
                    basic_info: 'Basic Info',
                    applications: 'Applications',
                },
                sections: {
                    info_config: 'Settings',
                    info_config_openid: 'OpenID Settings',
                    basic_auth: 'Basic Auth Settings',
                },
                actions: {
                    back: 'OAuth Clients',
                    delete: 'Delete',
                    add_application: 'Add Application',
                },
                bulk_delete: {
                    button: 'Delete (%{count})',
                    confirm_title: 'Confirm permanent deletion',
                    confirm_permanent:
                        '%{count} selected client(s) have status "Disabled" and will be permanently deleted along with all their application links. This action cannot be undone.',
                    confirm_soft:
                        '%{count} active client(s) will be soft-deleted (disabled).',
                    cancel: 'Cancel',
                    confirm: 'Delete',
                    success_disabled: '%{count} client(s) disabled',
                    success_permanent: '%{count} permanently deleted',
                    error: 'Error deleting clients',
                },
                applications: {
                    title: 'Application Links',
                    title_openid: 'OpenID Application Settings',
                    no_links: 'No application links',
                    fields: {
                        application: 'Application',
                        api_key_override: 'API Key Override',
                        redirect_uris: 'Redirect URIs',
                        login_uri: 'Login URI',
                        authorize_uris: 'Authorize URIs',
                        scope: 'Scope',
                        response_type: 'Response Type',
                        access_token_name: 'Access Token Name',
                        use_refresh_token: 'Use Refresh Token',
                        status: 'Status',
                        actions: 'Actions',
                    },
                    dialog: {
                        add_title: 'Add Application Link',
                        edit_title: 'Edit Application Link',
                        application_guid: 'Application GUID',
                        redirect_uris_helper: 'One URI per line',
                        uris_helper: 'One URI per line',
                        comma_separated_helper: 'Comma separated',
                        cancel: 'Cancel',
                        save: 'Save',
                        add: 'Add',
                    },
                    confirm_delete: 'Remove this application link?',
                    notify: {
                        removed: 'Application link removed',
                        updated: 'Application link updated',
                        added: 'Application link added',
                    },
                },
                audit: 'Audit',
            },
            error: 'Error loading data',
            ecs: {
                title: 'ECS Services',
                no_services: 'No services found',
                fields: {
                    serviceName: 'Service',
                    status: 'Status',
                    running: 'Running',
                    desired: 'Desired',
                    pending: 'Pending',
                    taskDefinition: 'Task Definition',
                    lastEvent: 'Last Event',
                },
            },
            rds: {
                title: 'RDS/Aurora Clusters',
                no_clusters: 'No clusters found',
                fields: {
                    cluster: 'Cluster',
                    status: 'Status',
                    engine: 'Engine',
                    endpoint: 'Endpoint (writer)',
                    readerEndpoint: 'Endpoint (reader)',
                    instances: 'Instances',
                },
            },
            rds_metrics: {
                title: 'RDS Performance',
                no_cluster: 'No clusters available',
                charts: {
                    cpu: 'CPU Utilization (%)',
                    connections: 'Database Connections',
                    readLatency: 'Read Latency',
                    writeLatency: 'Write Latency',
                    readIOPS: 'Read IOPS',
                    writeIOPS: 'Write IOPS',
                    freeableMemory: 'Freeable Memory',
                    networkReceive: 'Network Receive',
                    networkTransmit: 'Network Transmit',
                },
            },
        },
        pos: {
            search: 'Search',
            configuration: 'Settings',
            language: 'Language',
            theme: {
                name: 'Theme',
                light: 'Light',
                dark: 'Dark',
            },
            filter: 'Filtered by',
            dashboard: {
                welcome: {
                    title: 'Welcome to the new support interface.',
                    subtitle: 'This is the new support interface.',
                },
            },
        },
        resources: {
            user: {
                name: 'Administrator |||| Administrators |||| All administrators',
                fields: {
                    // group: 'Group',
                    // groups: 'Groups',
                    last_login: 'Last login',
                    registered: 'Registered',
                    name: 'Name',
                    username: 'Username',
                    status: 'Status',
                    password: 'Password',
                    public: {
                        firstname: 'First name',
                        middlename: 'Middle name',
                        lastname: 'Last name',
                        description: 'Description',
                        login_not_allowed: 'Login not allowed',
                        validfrom: 'Valid from',
                        validto: 'Valid to',
                        profile_locked: 'Profile locked',
                        profile_locked_message: 'Send unlock message',
                        profile_locked_description:
                            'If the unlock message is not sent, the user can request the unlock message themselves when trying to log in to the service.',
                    },
                    author: {
                        creator: 'Creator',
                        created: 'Created',
                        modifier: 'Modifier',
                        modified: 'Modified',
                    },
                    group: {
                        name: 'Name',
                        valid: 'Validity period',
                        valid_desc:
                            'Leave the fields empty if you do not want to set a validity period.',
                        ofduty: 'On leave',
                        isadmin: 'Administrator',
                        role: 'Role',
                        subrole: 'Teaching subjects',
                        status: 'Status',
                        joined: 'Joined',
                        no_classes: 'No classes',
                        show_archived: 'Show archived/removed',
                        archived: 'Archived',
                        deleted: 'Removed',
                        classes_count: 'Groups',
                        active_classes_count: 'Active groups',
                        archived_or_deleted_classes_count:
                            'Archived/removed groups',
                    },
                    applicationToAdd: 'Service to add',
                    application: {
                        name: 'Service',
                        isadmin: 'Administrator',
                        role: 'Role',
                        status: 'Status',
                    },
                },
                filters: {
                    status: 'Status',
                    status_close: 'Status',
                    schools: 'Schools',
                    'schools.search': 'Search schools',
                    schools_close: 'Schools',
                    invitation_delivery: 'Invitation delivery',
                },
                tabs: {
                    profile: 'Profile',
                    application: 'Services',
                    logs: 'Event log',
                },
                status: {
                    0: 'Active',
                    1: 'Removed',
                    2: 'Pending confirmation',
                    3: 'Unregistered',
                    10: 'Possible duplicate',
                },
                invitation: {
                    badge: 'Invitation',
                    title: 'Manage invitation',
                    pending: 'Invitation pending',
                    none: 'No pending invitations',
                    expired: 'expired',
                    delivery: {
                        label: 'Delivery',
                        delivered: 'Invitation delivered',
                        failed: 'Invitation delivery failed',
                        pending: 'Invitation sent, awaiting confirmation',
                        unknown: 'Delivery status unknown',
                        resend_exhausted:
                            'Auto-resend attempts exhausted (needs attention)',
                        show_events: 'Show Mailgun events',
                        error_category: 'Reason',
                        error_raw: 'Technical error',
                        errors: {
                            bad_domain:
                                'The email domain was not found – there is likely a typo in the address.',
                            access_denied:
                                'The recipient server rejected the message for access/policy reasons (e.g. the organization blocks external messages).',
                            no_mailbox:
                                'The recipient email address does not exist.',
                            mailbox_full: 'The recipient mailbox is full.',
                            mail_loop:
                                'The recipient’s mail forwarding is stuck in a loop (mail loop), so the message cannot be delivered.',
                            blocked:
                                'The message was blocked by the recipient server.',
                            other: 'Other delivery error (see technical message).',
                        },
                    },
                    dialog: {
                        title: 'Manage user invitation',
                        section_status: 'Invitation status',
                        manual_sends: 'Manual resends',
                        manual_send_source_admin: 'Otava customer service',
                        manual_send_source_nova: 'School administrator',
                        section_school: 'School',
                        section_link: 'Activation link',
                        content:
                            'Send invitation to user %{name} (%{username}) to this school',
                        confirm: 'Send invitation',
                        cancel: 'Cancel',
                        language: 'Language',
                        sent_count:
                            'Invitation has been sent %{count} times before',
                        not_sent: 'Invitation has not been sent yet',
                        first_sent: 'First invitation sent',
                        last_sent: 'Last invitation sent',
                        last_auto_resend:
                            'Last automatic resend (%{count} times)',
                        expired: 'The invitation has expired — send a new one.',
                        auto_resend_label: 'Automatic resend',
                        auto_resend_on: 'enabled',
                        auto_resend_off_disabled: 'disabled',
                        auto_resend_off_not_pending: 'no pending invitation',
                        auto_resend_off_exhausted: 'maximum resends reached',
                        auto_resend_off_expired: 'invitation too old (TTL)',
                        auto_resend_off_delivered:
                            'invitation already delivered',
                        auto_resend_off_permanent_error:
                            'address/domain permanently invalid',
                        auto_resend_next: 'Next resend no earlier than',
                        auto_resend_due: 'on the next run',
                        show_activation_link: 'Show activation link',
                        activation_link_help:
                            'The teacher’s personal activation link for confirming their email address and signing in to Nova. Use it if the invitation email did not arrive: deliver the link to the teacher via a trusted channel (e.g. the school’s own email or phone). It is valid for 24 hours from when the invitation was sent — if it has expired, send a new invitation.',
                    },
                    bulk_resend: 'Resend invitation',
                    bulk_confirm_title: 'Resend invitation',
                    bulk_confirm_content:
                        'Resend the invitation to %{count} selected teachers?',
                    bulk_result:
                        'Sent %{sent} · no pending %{no_pending} · invalid email %{invalid_email} · permanent bounce %{permanent_bounce} · errors %{errors}',
                    bulk_error: 'Bulk invitation resend failed',
                },
                session: {
                    remaining: 'Session remaining',
                    active_sessions: 'active sessions',
                    longest: 'longest',
                    confirm_remove:
                        'Are you sure you want to remove the session and log the user out?',
                    confirm_remove_all:
                        'Are you sure you want to remove all %{count} sessions and log the user out from all devices?',
                },
                stale: {
                    title: 'Data state',
                    up_to_date: 'Data up to date',
                    stale: 'Data stale',
                    last_checked: 'Last checked',
                    force_toggle: 'Force recheck / clear override',
                    natural_hint:
                        "Data is stale even without the override (last check too long ago) - clearing the override alone won't flip it back to up to date.",
                    forced: 'User forced to recheck their data',
                    cleared: 'Override cleared',
                    bulk_force: 'Force recheck (selected)',
                    bulk_confirm_title: 'Force data recheck',
                    bulk_confirm_content:
                        'Mark %{count} selected users as stale - they will need to recheck their data on next login?',
                    bulk_result: 'Marked stale: %{updated}/%{requested}',
                    bulk_error: 'Bulk force-stale failed',
                },
                help: {
                    modifyuserapp: 'Modify user service settings.',
                },
                actions: {
                    approve: 'Approve',
                    changepassword: 'Change password',
                    reapprove: 'Reapprove',
                    remove_mpass: 'Remove MPASS link',
                    remove_session: 'Remove session and log user out.',
                    debug_mpass_original:
                        'Debug: MPASS authentication (original)',
                    debug_mpass_new: 'Debug: MPASS authentication (new)',
                    debug_mpass_menu: 'Debug: MPASS tests',
                    debug_mpass_wrong_app:
                        'Debug: Test redirect from the wrong application with the same MPASS data',
                    debug_mpass_json: 'Debug: MPASS data',
                    mpass_profile: 'MPASS basic information',
                    debug_mpass_masked: 'Masked test (localhost)',
                    debug_mpass_masked_help: 'Names, personal identifiers and class names are synthetic. School, organization and role information remains real, so anonymity is not guaranteed. Check the test environment: login may create accounts and use its configured external integrations.',
                    mpass_name: 'Name from MPASS',
                    mpass_nickname: 'Nickname from MPASS',
                    mpass_name_changed: 'MPASS contains a name change record. Previous name and timestamp: %{previous}',
                    mpass_schools: 'Schools',
                    mpass_roles: 'Roles',
                    mpass_classes: 'Classes / groups',
                    mpass_last_login: 'Last recorded MPASS login',
                    debug_mpass_dialog_title:
                        'MPASS test authentication data',
                    debug_mpass_json_help:
                        'This is the MPASS profile sent for authentication. You can edit the JSON, including to test invalid data. The profile is not saved to the user.',
                    debug_mpass_start: 'Start MPASS test authentication',
                    debug_mpass_cancel: 'Cancel',
                    new: {
                        title: 'New user',
                    },
                    reset_password: {
                        title: 'Password reset link',
                        help_text:
                            'The password reset link is one-time use and expires after use. Note: Requesting a new reset link always invalidates all previously created, unused links.',
                        button_label: 'Password reset link',
                        description: `Hello!

Here is the link to reset your password: 
%{link}. 
Copy the link and open it in your browser. You will then be able to create a new password.

Best regards
Otava Learning
`,
                    },
                },
            },
            teacher: {
                name: 'Teacher |||| Teachers',
                actions: {
                    back: 'Back to training information',
                    approve: {
                        button_title: 'Approve',
                        dialog: {
                            title: 'Approve %{mode}',
                            content:
                                'You are approving user %{name} (%{username})',
                            schools:
                                'Schools to which the user will be attached',
                            sendmail: 'Send confirmation email',
                            cancel: 'Cancel',
                            confirm: 'Approve',
                            language: 'Language',
                        },
                        success: 'Action succeeded',
                        error: 'Approval failed',
                    },
                    remove: {
                        button_title: 'Remove',
                        dialog: {
                            title: 'Confirm %{mode} removal',
                            content:
                                'You are confirming removal of user %{name} (%{username})',
                            schools:
                                'Schools from which the user will be removed',
                            remove_groups:
                                'Remove user from groups of the schools being removed',
                            remove_user: 'Mark user (%{name}) as removed',
                            cancel: 'Cancel',
                            confirm: 'Approve',
                            language: 'Language',
                        },
                        success: 'Action succeeded',
                        error: 'Approval failed',
                    },
                    unremove: {
                        button_title: 'Restore',
                        success: 'Action succeeded',
                        error: 'Restore failed',
                    },
                    add_school: 'Add school',
                    export_duplicates: 'Export duplicates to Excel',
                    new: {
                        title: 'New teacher',
                        sendmail: 'Send message to teacher',
                        lang: 'Language',
                        application: 'Service',
                        messagepreview: 'Message preview',
                        nomailtemplate:
                            'The service does not have a function implemented. The message cannot be sent.',
                        mailtemplateinactive:
                            'The message template is not active. The message cannot be sent.',
                    },
                    changepassword: 'Change password',
                    reapprove: 'New approval request',
                },
                fields: {
                    last_login: 'Last login',
                    registered: 'Registered',
                    joined: 'Joined',
                    modified: 'Modified',
                    name: 'Name',
                    username: 'Work email address',
                    username_column: 'Username',
                    status: 'Status',
                    password: 'Password',
                    validfrom: 'Valid from',
                    validto: 'Valid to',
                    public: {
                        firstname: 'First name',
                        middlename: 'Middle name',
                        lastname: 'Last name',
                        description: 'Description',
                        learning_environment_1: 'Google Classroom',
                        learning_environment_2: 'Office 365',
                        learning_environment_3: 'Kampus',
                        learning_environment_4: 'Moodle',
                        learning_environment_5: 'Itslearning',
                        learning_environment_6: 'Peda.net',
                        learning_environment_free: 'Some other, what?',
                        login_not_allowed: 'Login not allowed',
                        validfrom: 'Valid from',
                        validto: 'Valid to',
                        profile_locked: 'Profile locked',
                        'infra:merged': 'Merged',
                    },
                    author: {
                        creator: 'Creator',
                        created: 'Created',
                        modifier: 'Modifier',
                        modified: 'Modified',
                    },
                    schools: 'Schools',
                    groups: 'Groups',
                    options: 'Options',
                    school: {
                        title: 'School',
                        registered: 'Registered',
                        settings: 'Settings',
                        group: 'Group',
                    },
                    group: {
                        valid: 'Validity period',
                        valid_desc:
                            'Leave the fields empty if you do not want to set a validity period.',
                        ofduty: 'On leave',
                        isadmin: 'Permissions to update school information',
                        role: 'Role',
                        subrole: 'Subjects',
                    },
                    role: {
                        guid: 'Role',
                        subjects: 'Subjects',
                    },
                    application: 'Service',
                    services: 'Sources',
                },
                tabs: {
                    profile: 'Basic Information',
                    schools_and_groups: 'Schools and Groups',
                    materials: 'In Use',
                    merge: 'Merging',
                    schools: 'Schools',
                    classes: 'Classes',
                    role: 'Role',
                    subject: 'Subjects',
                    isadmin: 'Administrator',
                    ofduty: 'On Leave',
                    logs: 'Logs',
                    status: 'Status',
                    application: 'Services',
                },
                cache: {
                    label: 'Valkey cache',
                    cached: 'In cache',
                    not_cached: 'Not in cache',
                    ttl_remaining: 'Time remaining',
                    expires_at: 'Expires',
                    refresh: 'Re-check cache status',
                    view_data: 'View cached data',
                    flush: 'Clear cached data',
                    flush_confirm:
                        'Clear the teacher full data cache key %{key}? The next partner API request will rebuild it.',
                    flushed: 'Cache cleared',
                    flush_error: 'Clearing the cache failed',
                },
                page: {
                    delete: 'Delete teacher',
                    create: 'Add teacher',
                    addSchool: 'Add school',
                    addApp: 'Add service',
                    changepassword: {
                        title: 'Change password',
                        oldpassword: 'Old password',
                        newpassword: 'Enter new password',
                        responses: {
                            401: 'Old password is incorrect',
                            404: 'User not found',
                            500: 'Password change failed, please try again later.',
                            200: 'Password has been changed',
                        },
                    },
                },
                status: {
                    0: 'Active |||| Active',
                    1: 'Deleted |||| Deleted',
                    2: 'Pending Confirmation |||| Pending Confirmation',
                    3: 'Unregistered |||| Unregistered',
                },
                help: {
                    createform:
                        'Create a new teacher and link them to a school. The account is valid indefinitely unless a validity period is specified.',
                    modifyschoolform: "Edit the teacher's school information.",
                },
                empty_header: 'No teachers',
            },
            student: {
                name: 'Student |||| Students',
                actions: {
                    back: 'Back to school information',
                    changepassword: 'Change password',
                    reapprove: 'Resend confirmation request',
                    gdpr: {
                        button_title: 'GDPR Removal',
                        success: 'GDPR removal succeeded',
                        error: 'GDPR removal failed',
                        dialog: {
                            title: 'GDPR Removal',
                            content:
                                'You are deleting student %{name} (%{email}). This action cannot be undone.',
                            describe_service:
                                'Deletion request for %{service} with ID: %{id}',
                            extra_content:
                                'NOTE! Save GDPR deletion requests before deleting.',
                            confirm: 'Delete',
                            cancel: 'Cancel',
                        },
                    },
                    new: {
                        title: 'New student',
                    },
                    export: {
                        10: 'Export duplicates to Excel',
                    },
                    approve: {
                        button_title: 'Approve',
                        dialog: {
                            title: 'Approve %{mode}',
                            content:
                                'You are approving user %{name} (%{username})',
                            schools: 'Schools the user will be linked to',
                            sendmail: 'Send confirmation email',
                            cancel: 'Cancel',
                            confirm: 'Approve',
                            language: 'Language',
                        },
                        success: 'Action succeeded',
                        error: 'Approval failed',
                    },
                    remove: {
                        button_title: 'Remove',
                        dialog: {
                            title: 'Confirm %{mode} removal',
                            content:
                                'You are confirming the removal of user %{name} (%{username})',
                            schools:
                                'Schools from which the user will be removed',
                            remove_groups:
                                'Also remove user from groups in the removed schools',
                            remove_user: 'Also mark user (%{name}) as removed',
                            cancel: 'Cancel',
                            confirm: 'Confirm',
                            language: 'Language',
                        },
                        success: 'Action succeeded',
                        error: 'Confirmation failed',
                    },
                },
                fields: {
                    last_login: 'Last login',
                    registered: 'Registered',
                    joined: 'Joined',
                    modified: 'Modified',
                    name: 'Name',
                    username: 'Textbook service username',
                    status: 'Status',
                    password: 'Password',
                    email: 'Email address',
                    alt_email: 'Alternative email addresses',
                    materialcharge: {
                        title: 'Material fee status',
                        no: 'No',
                        yes: 'Yes',
                        unknown: 'Unknown',
                    },
                    school: {
                        title: 'School',
                        registered: 'Joined',
                    },
                    public: {
                        email: 'Email address',
                        emails: 'Alternative email addresses',
                        firstname: 'First name',
                        middlename: 'Middle name',
                        lastname: 'Last name',
                        description: 'Description',
                        login_not_allowed: 'Disallow login',
                        validfrom: 'Valid from',
                        validto: 'Valid to',
                        profile_locked: 'Account locked',
                        'infra:merged': 'Merged',
                    },
                    author: {
                        creator: 'Creator',
                        created: 'Created',
                        modifier: 'Modifier',
                        modified: 'Modified',
                    },
                    group: {
                        valid: 'Account validity period',
                        valid_desc:
                            'Leave fields empty if you do not want to set a validity period.',
                        validfrom: 'Start date',
                        validto: 'End date',
                        ofduty: 'On leave',
                        isadmin: 'Permission to update school info',
                        role: 'Role',
                        subrole: 'Subjects',
                    },
                    application: 'Service',
                    services: 'Sources',
                },
                tabs: {
                    profile: 'Basic information',
                    schools_and_groups: 'Schools and Groups',
                    materials: 'In Use',
                    merge: 'Merging',
                    schools: 'Schools',
                    classes: 'Classes',
                    role: 'Role',
                    subject: 'Subjects',
                    isadmin: 'Administrator',
                    ofduty: 'On Leave',
                    logs: 'Logs',
                    status: 'Status',
                    application: 'Services',
                },
                page: {
                    delete: 'Delete teacher',
                    create: 'Add teacher',
                    addSchool: 'Add school',
                    addApp: 'Add service',
                    changepassword: {
                        title: 'Change password',
                        oldpassword: 'Old password',
                        newpassword: 'Enter new password',
                        responses: {
                            401: 'Old password incorrect',
                            404: 'Invalid user',
                            500: 'Password change failed, please try again later.',
                            200: 'Password has been changed',
                        },
                    },
                },
                status: {
                    0: 'Active |||| Active',
                    1: 'Deleted |||| Deleted',
                    2: 'Pending confirmation |||| Pending confirmation',
                    3: 'Unregistered |||| Unregistered',
                },
                help: {
                    createform:
                        'Create a new teacher and link them to a school. The account is valid indefinitely unless a validity period is set.',
                    modifyschoolform: 'Edit teacher’s school information.',
                },
                empty_header: 'No students',
            },
            pupil: {
                name: 'Pupil |||| Pupils',
                codes: [
                    'aalto',
                    'aamu',
                    'aarre',
                    'ahven',
                    'aine',
                    'aisti',
                    'aito',
                    'akseli',
                    'alasin',
                    'alku',
                    'alpakka',
                    'askel',
                    'atlantti',
                    'atomi',
                    'aurinko',
                    'avain',
                    'elo',
                    'hanko',
                    'happi',
                    'harakka',
                    'harju',
                    'hattu',
                    'hauki',
                    'haukka',
                    'heitto',
                    'helmi',
                    'herhiläinen',
                    'hiekka',
                    'hiili',
                    'hiiri',
                    'hiutale',
                    'hopea',
                    'huhta',
                    'huhti',
                    'huippu',
                    'hukka',
                    'hylly',
                    'hymy',
                    'höyhen',
                    'ikkuna',
                    'ilo',
                    'ilta',
                    'ilves',
                    'into',
                    'jalava',
                    'jalo',
                    'joki',
                    'jono',
                    'jousi',
                    'juhla',
                    'juuri',
                    'jälki',
                    'jälki',
                    'kaarre',
                    'kaiku',
                    'kakku',
                    'kala',
                    'kallio',
                    'kameli',
                    'kani',
                    'kaniini',
                    'kansi',
                    'karhu',
                    'kartta',
                    'karviainen',
                    'kasse',
                    'kasvi',
                    'kaura',
                    'kauris',
                    'keino',
                    'keinu',
                    'kello',
                    'keltainen',
                    'kerho',
                    'kerros',
                    'kesä',
                    'kettu',
                    'kevät',
                    'kielo',
                    'kierros',
                    'kierto',
                    'kimalainen',
                    'kipinä',
                    'kirahvi',
                    'kirja',
                    'kirjain',
                    'kirjasto',
                    'kirsu',
                    'kissa',
                    'kivi',
                    'koira',
                    'koivu',
                    'koju',
                    'kontio',
                    'korento',
                    'kori',
                    'koti',
                    'koulu',
                    'kuikka',
                    'kuitu',
                    'kukka',
                    'kulku',
                    'kulma',
                    'kulta',
                    'kuohu',
                    'kupari',
                    'kurki',
                    'kurre',
                    'kuu',
                    'kuusi',
                    'kuva',
                    'kyyhky',
                    'käpy',
                    'käpälä',
                    'lahja',
                    'lahti',
                    'laine',
                    'laituri',
                    'laiva',
                    'lammas',
                    'lasi',
                    'lasku',
                    'latva',
                    'laulu',
                    'laulu',
                    'lehti',
                    'lehto',
                    'leijona',
                    'leikki',
                    'leipä',
                    'lento',
                    'leopardi',
                    'leppä',
                    'liekki',
                    'liina',
                    'lintu',
                    'lippu',
                    'lohi',
                    'loiste',
                    'luku',
                    'lumi',
                    'lumo',
                    'luokka',
                    'luonto',
                    'maa',
                    'maailma',
                    'maasto',
                    'maila',
                    'majakka',
                    'manner',
                    'mansikka',
                    'marmori',
                    'matka',
                    'mehiläinen',
                    'meri',
                    'metsä',
                    'miekkavalas',
                    'mustikka',
                    'muurahainen',
                    'myrsky',
                    'mäki',
                    'nappi',
                    'nauha',
                    'neptunus',
                    'niemi',
                    'niitty',
                    'numero',
                    'nummi',
                    'nuotio',
                    'oiva',
                    'oksa',
                    'olki',
                    'omena',
                    'onki',
                    'onni',
                    'opas',
                    'oppi',
                    'orava',
                    'otso',
                    'ovi',
                    'paiste',
                    'pala',
                    'pallo',
                    'paperi',
                    'pelikaani',
                    'perho',
                    'peura',
                    'pihlaja',
                    'pino',
                    'pinta',
                    'pisara',
                    'pluto',
                    'pohjanmeri',
                    'polku',
                    'portti',
                    'pronssi',
                    'puhe',
                    'puhuri',
                    'punainen',
                    'puomi',
                    'purje',
                    'puro',
                    'puu',
                    'pyy',
                    'päivä',
                    'raikas',
                    'raita',
                    'raita',
                    'ranta',
                    'rastas',
                    'rata',
                    'ratas',
                    'rauta',
                    'reitti',
                    'repo',
                    'reppu',
                    'retki',
                    'riemu',
                    'ruis',
                    'rusko',
                    'ruusu',
                    'sade',
                    'sakset',
                    'salmi',
                    'sammal',
                    'sarja',
                    'sato',
                    'sauna',
                    'savu',
                    'siipi',
                    'silta',
                    'simpukka',
                    'sininen',
                    'sisko',
                    'sivu',
                    'sointu',
                    'soitin',
                    'solki',
                    'sudenkorento',
                    'sulka',
                    'sumu',
                    'syksy',
                    'syksy',
                    'syys',
                    'sävel',
                    'taika',
                    'taimen',
                    'taimi',
                    'taito',
                    'taituri',
                    'talo',
                    'talvi',
                    'tammi',
                    'tanner',
                    'tarha',
                    'tasku',
                    'tassu',
                    'telkkä',
                    'tellus',
                    'tenho',
                    'teos',
                    'tiainen',
                    'tie',
                    'tieto',
                    'tiikeri',
                    'tiira',
                    'tikka',
                    'tikkari',
                    'tikli',
                    'tilke',
                    'timantti',
                    'tina',
                    'torni',
                    'touko',
                    'tovi',
                    'tuli',
                    'tunti',
                    'tunturi',
                    'tuohi',
                    'tuokio',
                    'tuoli',
                    'tuomi',
                    'tuore',
                    'tupa',
                    'tuuli',
                    'tyvi',
                    'tähti',
                    'täky',
                    'uikku',
                    'ukko',
                    'ulappa',
                    'usva',
                    'vaaka',
                    'vadelma',
                    'vahti',
                    'valkoinen',
                    'valo',
                    'varpunen',
                    'vasta',
                    'vasu',
                    'vene',
                    'vene',
                    'veto',
                    'vety',
                    'vihreä',
                    'vihta',
                    'viima',
                    'viiri',
                    'vimma',
                    'violetti',
                    'virta',
                    'voima',
                    'vuori',
                    'vuoro',
                    'välke',
                ],
                actions: {
                    new: {
                        title: 'New pupil',
                    },
                    export: {
                        '10': 'Export duplicates to Excel',
                    },
                    approve: {
                        button_title: 'Confirm',
                        dialog: {
                            title: 'Confirm %{mode}',
                            content:
                                'You are confirming the user %{name} (%{username})',
                            schools: 'Schools the user will be associated with',
                            sendmail: 'Send confirmation email',
                            cancel: 'Cancel',
                            confirm: 'Confirm',
                            language: 'Language',
                        },
                        success: 'Action successful',
                        error: 'Confirmation failed',
                    },
                    remove: {
                        button_title: 'Delete',
                        dialog: {
                            title: 'Confirm %{mode} removal',
                            content:
                                'You are confirming the user %{name} (%{username})',
                            schools:
                                'Schools from which the user will be removed',
                            remove_groups:
                                'Also remove user from groups in the removed schools',
                            remove_user: 'Also mark user (%{name}) as removed',
                            cancel: 'Cancel',
                            confirm: 'Confirm',
                            language: 'Language',
                        },
                        success: 'Action successful',
                        error: 'Confirmation failed',
                    },
                },
                fields: {
                    last_login: 'Logged in',
                    registered: 'Registered',
                    joined: 'Joined',
                    modified: 'Modified',
                    name: 'Name',
                    username: 'Email',
                    license_status: 'License status',
                    licenses: 'In use',
                    password: 'Password',
                    public: {
                        firstname: 'First name',
                        middlename: 'Middle name',
                        lastname: 'Last name',
                        code: 'Key code',
                        login_not_allowed: 'Disable login',
                        validfrom: 'Starts',
                        validto: 'Ends',
                        profile_locked: 'Account locked',
                    },
                    school: {
                        title: 'School',
                        registered: 'Joined',
                    },
                    schools: 'Schools',
                    groups: 'Groups',
                    class: 'Class',
                    group_name: 'Class',
                    author: {
                        creator: 'Creator',
                        created: 'Created',
                        modifier: 'Modifier',
                        modified: 'Modified',
                    },
                    services: 'Sources',
                },
                tabs: {
                    profile: 'Basic info',
                    schools_and_groups: 'Schools and groups',
                    materials: 'In use',
                    merge: 'Merge',
                    schools: 'Schools',
                    classes: 'Classes',
                    role: 'Role',
                    subject: 'Subjects',
                    isadmin: 'Administrator',
                    ofduty: 'Off duty',
                    logs: 'Log',
                    status: 'Status',
                    application: 'Services',
                },
                page: {
                    add_pupils_title: 'Add pupils to class',
                    delete: 'Delete pupil',
                    create: 'Add pupil',
                    fromfile: 'Add from CSV file',
                },
                status: {
                    '0': 'Active',
                    '1': 'Deleted',
                    '2': 'Registration incomplete',
                    '3': 'Unregistered',
                },
                help: {
                    never_used: 'Never logged in',
                    createform:
                        'A key code is created for the pupil during the addition.',
                    createnewkey: 'Create a new key code for the pupil',
                },
                empty_header: 'No pupils',
            },
            events: {
                name: 'Event |||| Events',
            },
            transfer: {
                name: 'Transfer/Merging |||| Transfer/Mergings',
                fields: {
                    name: 'Icon',
                    item_type: 'Type code',
                    item_type_name: 'Type',
                    description_field1: 'Description 1',
                    description_field2: 'Description 2',
                    description_field3: 'Description 3',
                },
                filters: {
                    category: 'Series',
                    status: 'Status',
                    type: 'Type',
                    languages: 'Languages',
                    target_persons: 'Target group',
                    kouluasteet: 'School level',
                    applications: 'Applications',
                },
            },
            subproduct: {
                empty_header: 'No sub-products',
                empty_message: 'Add sub-products using the form below.',
            },
            product: {
                name: 'Product |||| Products',
                fields: {
                    id: 'Order link',
                    name: 'Name',
                    'public.alternative_name': 'Alternative name',
                    'private.tuoterekisteri_data.tuotenimi':
                        'Name in product register',
                    'public.category': 'Book series',
                    'public.languages': 'Languages',
                    'public.target_persons': 'Target group',
                    'public.show_testusage_button': 'Show trial button',
                    'public.kouluasteet': 'School level',
                    'public.applications': 'Applications where shown',
                    status: 'Status',
                    'public.product_type': 'Product type',
                    'public.modules': 'Modules',
                    'public.not_period_license':
                        'Not a seasonal license product',
                    is_group: 'Group',
                    'public.materialstorage.isbn': 'ISBN',
                    'public.materialstorage.alternative_isbn':
                        'ISBN (tolerant)',
                    path: 'Path',
                    filename: 'File',
                    show_only_in_list: 'Visibility',
                    show_only_in_list_input:
                        'Do not show as individual product',
                    show_only_to_subscribers: 'Show only to subscribers',
                    'public.dont_show_as_main_product':
                        'Show as main product only to subscribers',
                    'public.dont_show_as_sub_product':
                        'Do not show as sub-product in Nova',
                    free_to_download: 'Product is free',
                    'public.default_license_type': 'License type',
                    'public.default_license_period': 'Default license duration',
                    'public.disable_extra_material':
                        'Do not allow activation with ISBN',
                    'public.is_saamelaiskaraja': 'Sámi Parliament product',
                    'public.materialstorage.default_trial_period':
                        'Default trial period',
                    'public.materialstorage.mime_type': 'File type',
                    'public.materialstorage.path': 'Path',
                    materialstorage_guid: 'Material storage',
                    description: 'Intro/Marketing text',
                    'private.tuoterekisteri_data.saatavuus': 'Availability',
                    'private.tuoterekisteri_data.tl_tuote_esittelyteksti_info':
                        'Intro/Marketing text',
                    'private.tuoterekisteri_data.kantanumero': 'Root number',
                    'public.subject': 'Subject',
                    'public.additional_subjects': 'Other subjects',
                    'public.imprint': 'Publisher',
                    'public.course': 'Course',
                    'public.element': 'Element',
                    'public.class_level': 'Grade level',
                    'public.productgroup': 'Product group',
                    'public.is_for_extra': 'Part of high school user benefits',
                    'public.is_package': 'Package product',
                    is_for_extra_first:
                        'Part of first year high school user benefits',
                    'public.show_high_school_groups':
                        'Part of high school group selection',
                    'public.release_date': 'Additional info',
                    'public.free_to_download': 'Free',
                    'public.free_for_extra': 'For existing users',
                    order: 'Sort',
                    'public.sumplattava': 'Product is mixable',
                    'public.analytics': 'Product has analytics',
                    saatavuus: 'Availability',
                    modules: 'Modules',
                    'public.extendable': 'License is extendable',
                    'public.extendable_days': 'Extension time in days',
                    'user.firstname': 'First name',
                    'user.lastname': 'Last name',
                    'user.email': 'Email',
                    'license.valid_from': 'From',
                    'license.valid_until': 'Until',
                    'subscriber.name': 'Subscriber',
                    'subscription.name': 'Shared / Subscribed',
                    preview: 'Preview',
                },
                filters: {
                    category: 'Series',
                    category_close: 'Series',
                    status: 'Status',
                    status_close: 'Status',
                    type: 'Type',
                    type_close: 'Type',
                    languages: 'Language',
                    languages_close: 'Language',
                    target_persons: 'Target group',
                    target_persons_close: 'Target group',
                    kouluasteet: 'School level',
                    kouluasteet_close: 'School level',
                    applications: 'Applications',
                    applications_close: 'Applications',
                    materialstorage: 'Material storage',
                    materialstorage_close: 'Material storage',
                    kvstatus: 'Availability',
                    kvstatus_close: 'Availability',
                },
                tabs: {
                    details: 'Basic info',
                    extra_details: 'Additional info',
                    redirect_details: 'Redirect info',
                    tuotelista_details: 'Product register info',
                    history_details: 'History',
                    sub_details: 'Linked products',
                    modify_history: 'Change history',
                    usage_history: 'Usage history',
                    licenses: 'Licenses',
                },
            },
            subscription: {
                name: 'Order |||| Orders',
                fields: {
                    name: 'Name',
                    subscriber: 'Subscriber',
                    product: 'Product',
                    product_guid: 'Product',
                    subscriber_guid: 'Subscriber',
                    valid_from: 'From',
                    valid_until: 'Until',
                    valid_season: 'Valid',
                    license_count: 'Purchased',
                    used_license_count: 'Used',
                    used_license_teacher_count: 'In use',
                    unused_license_count: 'Available',
                    type: 'License type',
                    product_code: 'Product code',
                    activate_code: 'Activation code',
                    message: 'Reason for change',
                    license_user_name: 'User',
                    license_created_date: 'Activation',
                    license_modified_date: 'Modified',
                    status: 'Status',
                    srcond: 'Targeting',
                    created_start: 'Created (from)',
                    created_end: 'Created (to)',
                    modified_start: 'Modified (from)',
                    modified_end: 'Modified (to)',
                    created: 'Created',
                    modified: 'Modified',
                    'product.name': 'Product',
                    'subscriber.name': 'Subscriber',
                    'public.kausi': 'Season',
                    'public.kvean': 'EAN',
                    'public.kvnro': 'Order number(s)',
                    'public.kvsource': 'Source',
                    'public.kvetumaara': 'Front amount',
                    'public.kvkoulukoodi': 'School code',
                    'public.kvtilausmaara': 'Order quantity',
                    combination_of_licenses: 'Til./Etu/Muu',
                },
                filters: {
                    status: 'Status',
                    status_close: 'Status',
                    products: 'Product',
                    'products.search': 'Enter search terms',
                    products_close: 'Product',
                    subscribers: 'Subscriber',
                    'subscribers.search': 'Enter search terms',
                    subscribers_close: 'Subscriber',
                    isvalid: 'Validity',
                    isvalid_close: 'Validity',
                    activationcode: 'Activation code',
                    activationcode_search: 'Enter activation code',
                    activationcode_found:
                        'Activation code has been used and subscription found',
                    activationcode_not_found:
                        'No subscription found for this activation code in Storia',
                    activationcode_not_found_title:
                        'Activation code - not found',
                    activationcode_not_activated:
                        'Product has not been activated yet',
                    activationcode_not_activated_title:
                        'Activation code - not activated',
                    activationcode_activated_title:
                        'Activation code - activated',
                    activationcode_activated_at: 'Activated',
                    activationcode_invalid:
                        'Invalid activation code format (e.g. YWA4-32HF-YFVJ-9EK4-B4R7)',
                },
                tabs: {
                    details: 'Basic info',
                    license_details: 'In use',
                    messages: 'Notes',
                    public_details: 'Guidance info',
                    private_details: 'Product register info',
                    log: 'Event log',
                    modify_history: 'Change history',
                    usage_history: 'Usage history',
                    groups: 'Groups',
                    kv: 'Book distribution',
                },
                page: {
                    add_license: 'Share license',
                },
                action: {
                    accept: 'Add one',
                    reject: 'Remove one',
                },
                actions: {
                    remove: {
                        title: 'Obs !',
                    },
                },
                notification: {
                    approved_success: 'Addition successful',
                    approved_error: 'Error: Addition failed',
                    rejected_success: 'Removal successful',
                    rejected_error: 'Error: Removal failed',
                },
                license_status: {
                    0: 'In use',
                    1: 'Removed',
                    2: 'Granted',
                },
                license_action: {
                    delete: 'Delete',
                    unuse: 'Release',
                },
                status: {
                    0: 'Public',
                    1: 'Removed',
                },
                srcond: {
                    1: 'Unattributed',
                    2: 'Attributed',
                },
            },
            userlicense: {
                name: 'License |||| Licenses',
                fields: {
                    lastname: 'Last name',
                    firstname: 'First name',
                    email: 'Email',
                    product: 'Product name',
                    product_isbn: 'ISBN',
                    licenses: 'Licenses',
                    groups: 'Select group',
                },
            },
            userlist: {
                name: 'User |||| Users',
                fields: {
                    lastname: 'Last name',
                    firstname: 'First name',
                    middlename: 'Middle name',
                    username: 'Username',
                    email: 'Email',
                    role: 'Role',
                    subjects: 'Subjects',
                    code: 'Key code',
                },
            },
            school: {
                label: 'School',
                name: 'School |||| Schools',
                fields: {
                    empty: '(Not provided)',
                    empty_web_code:
                        'Web shop code not found, contact Otava customer service',
                    category_id: 'Category',
                    name: 'Name',
                    keyfields: 'Key details',
                    keyfields_more:
                        'Key details (school code, type, and location)',
                    material: 'Material',
                    public: {
                        hidepages: 'Hide pages from users',
                        categories: 'Series',
                        features: 'Debug features',
                        address: 'Mailing address',
                        city: 'City',
                        code: 'School code',
                        name: 'Name',
                        type: 'School type',
                        school_email: 'School official email rule',
                        phone_1: 'Teachers’ lounge phone 1',
                        phone_2: 'Teachers’ lounge phone 2',
                        location: 'Location',
                        zip_code: 'Postal code',
                        grade_max: 'Highest grade level',
                        grade_min: 'Lowest grade level',
                        headmaster: 'Principal',
                        identifier: 'Identifier',
                        shop_password: 'Password',
                        visit_city: 'Visit city',
                        last_checked: 'Last checked',
                        office_phone: 'Office phone',
                        visit_address: 'Visit address',
                        visit_zip_code: 'Visit postal code',
                        headmaster_email: 'Principal email',
                        headmaster_phone: 'Principal phone',
                        headmaster_cellphone: 'Principal mobile',
                        number_of_pupils: 'Number of pupils',
                        number_of_teachers: 'Number of teachers',
                        grades: 'Grade levels',
                        web_code: 'Web shop code',
                    },
                    'private.services.crm.crmGUID': 'CRM link',
                },
                filters: {
                    types: 'School types',
                    types_close: 'School types',
                    type_groups: 'School type groups',
                    type_groups_close: 'School type groups',
                    status: 'Status',
                    status_close: 'Status',
                    adminstatus: 'Administrators',
                    adminstatus_close: 'Administrators',
                    teacherstatus: 'Teachers',
                    teacherstatus_close: 'Teachers',
                    pupilstatus: 'Pupils',
                    pupilstatus_close: 'Pupils',
                    studentstatus: 'Students',
                    studentstatus_close: 'Students',
                    subscriptionstatus: 'Subscriptions',
                    subscriptionstatus_close: 'Subscriptions',
                },
                actions: {
                    show_web_shop_code: 'Show web shop code',
                    remove: {
                        title: 'Note!',
                        ask: 'Remove user from school %{name}',
                        description:
                            'Do you want to remove the user from school %{name}?',
                    },
                    add: {
                        title: 'Note!',
                        ask: 'Restore user to school %{name}',
                        description:
                            'Do you want to restore the user to school %{name}?',
                    },
                },
                page: {
                    manage_license: 'Pupil licenses',
                    pupil_license: {
                        addall: 'Select all',
                        removeall: 'Remove selections',
                        chosen: 'Selected',
                    },
                    student_license: {
                        code: 'Activation code',
                        name: 'Name',
                        aktivated: 'Activated',
                    },
                    web_code: 'Web shop code is used in Otava’s web store.',
                    web_code_username: 'Username: %{username}',
                    web_code_password: 'Password: %{password}',
                    orders: {
                        pupil: 'School’s pupil licenses',
                        school: 'School’s educational materials and digital book licenses',
                        past: 'Expired',
                        active: 'Active',
                        future: 'Upcoming',
                    },
                },
                help: {
                    manage_license: {
                        product: 'Material',
                        hint: 'Select material',
                        beforeProductSelected:
                            'Start assigning pupil licenses by selecting a material from the dropdown menu below.',
                        afterProductSelected:
                            'Select pupils from the list to assign licenses to. Don’t forget to save your changes by clicking the Save button. Pupils are listed by class. You can also add new pupils to classes by clicking the Add pupil button for each class.',
                        afterProductSelected2:
                            'You can also remove a pupil license from a pupil who has not yet used the material.',
                    },
                },
                tabs: {
                    details: 'Basic information',
                    subscription: 'Subscriptions',
                    groups: 'Groups',
                    teachers: 'Teachers',
                    pupils: 'Pupils',
                    students: 'Students',
                    stats: 'Statistics',
                },
                organization: {
                    not_found:
                        'Organization information not found from Opintopolku service. Please check the school code.',
                    error: 'Error fetching organization information',
                    inactive: 'Organization is not active',
                },
            },
            group: {
                name: 'Group |||| Groups',
                fields: {
                    name: 'Name',
                    status: 'Status',
                    parent_group: 'School',
                    schools_name: 'Schools',
                    'public.code': 'Join code',
                    'public.group_type': 'Group type',
                    'public.archived': 'Archived',
                    settings: {
                        allowOnlySameSchool: 'Allow Only Same School',
                        allowOnlyMPASS: 'Allow Only MPASS',
                    },
                    author: {
                        creator: 'Creator',
                        created: 'Created',
                        modifier: 'Modifier',
                        modified: 'Modified',
                    },
                    archived: 'Archived',
                },
                tabs: {
                    details: 'Basic information',
                    teachers: 'Teachers',
                    pupils: 'Pupils',
                    students: 'Students',
                    settings: 'Settings',
                },
                filters: {
                    status: 'Status',
                    status_close: 'Status',
                    archived: 'Archive',
                    archived_close: 'Archive',
                    schools: 'Schools',
                    schools_close: 'Schools',
                    'schools.search': 'Search school',
                },
            },
            usergroup: {
                empty_header: 'No groups',
                filters: {
                    onlyactive: 'Only active groups',
                },
                status: {
                    0: 'Active',
                    1: 'Removed',
                },
            },
            campaign: {
                name: 'Campaign |||| Campaigns',
                filters: {
                    search: 'Search by campaign name',
                    all: 'All',
                    ongoing: 'Ongoing',
                    upcoming: 'Upcoming',
                    ended: 'Ended',
                    inactive: 'Inactive',
                    period: 'Valid during selected period',
                    from: 'From date',
                    until: 'Until date',
                    invalid_dates: 'The end date must not precede the start date.',
                    period_help: 'Shows campaigns overlapping any part of the selected period. Dates use Helsinki time.',
                },
                fields: {
                    name: 'Campaign name',
                    start_date: 'Starts',
                    end_date: 'Ends',
                    books: 'Books',
                    isbn: 'ISBN',
                    title: 'Book title',
                    targets: 'Targets',
                    target_type: 'Target identifier type',
                    target_list: 'Target list',
                    created: 'Created',
                    csv_email: 'CSV export recipient',
                    csv_weekdays: 'Export weekdays (ma,ti,ke,to,pe,la,su)',
                    csv_time: 'Export time (Helsinki time, HH:mm)',
                    settings_json: 'Other campaign settings (JSON)',
                    export_schedule: 'CSV export',
                    target_count: 'Targets',
                    responded_count: 'Responded',
                    response_rate: 'Response rate',
                    accepted_count: 'Accepted',
                    rejected_count: 'Rejected',
                    user_guid: 'User GUID',
                    user_name: 'Teacher',
                    school_guid: 'School GUID',
                    school_name: 'School',
                    selected_books: 'Selected ISBNs',
                    responded_at: 'Responded at',
                    status: 'Status',
                },
                actions: {
                    template: 'Excel template',
                    export_workbook: 'Download campaign as an Excel template',
                    import: 'Import campaign',
                    export: 'Download accepted orders as CSV',
                    exporting: 'Downloading…',
                    export_responses: 'Download all responses as CSV',
                    exporting_responses: 'Downloading responses…',
                    email: 'Email accepted orders',
                    emailing: 'Sending…',
                },
                status: {
                    active: 'Active',
                    inactive: 'Inactive',
                    pending: 'Pending',
                    accepted: 'Accepted',
                    rejected: 'Rejected',
                },
                target_types: {
                    user_guid: 'Nova user GUID',
                    crm_guid: 'CRM GUID',
                },
                helpers: {
                    target: 'Enter one GUID per line. Select above whether the identifiers are Nova user GUIDs or CRM GUIDs.',
                    status: 'Active campaigns are visible to teachers within their date range. Inactive campaigns are hidden, cannot receive responses and do not send scheduled CSV exports. Manual exports remain available.',
                    csv_email: 'Email address for sending accepted campaign orders as a CSV file. Leave blank to use the default recipient configured in admin settings.',
                    weekdays: 'Use Finnish abbreviations ma,ti,ke,to,pe,la,su (Monday–Sunday), separated by commas. Blank means manual export only.',
                    time: '24-hour Europe/Helsinki local time.',
                    settings: 'Other campaign settings as a JSON object.',
                },
                tabs: {
                    details: 'Campaign details',
                    responses: 'Targets and responses',
                    daily_report: 'Daily report',
                    export_history: 'Export history',
                },
                daily_report: {
                    date: 'Date',
                    responses: 'Responses by day',
                    books: 'Selected books by day',
                    selected_books: 'Total books',
                    total: 'Total',
                    empty: 'This campaign has no responses yet.',
                    description: 'Response dates use Europe/Helsinki time. Book counts show how many accepted respondents selected each ISBN. Only days with responses are shown.',
                },
                delivery_report: {
                    time: 'Time',
                    type: 'Delivery type',
                    status: 'Status',
                    attempts: 'Attempts',
                    error_detail: 'Error',
                    empty: 'This campaign has no CSV delivery history yet.',
                    error: 'Could not load delivery history.',
                    description: 'Lists scheduled cron jobs and manual CSV emails. Sent means Mailgun accepted the message; final recipient delivery is not confirmed.',
                    types: {
                        scheduled: 'Scheduled cron',
                        manual: 'Manual',
                    },
                    statuses: {
                        sending: 'Sending',
                        sent: 'Sent',
                        failed: 'Failed',
                    },
                },
                import_success: 'Campaign imported from Excel.',
                template_error: 'Could not download the Excel template.',
                workbook_error: 'Campaign Excel export failed.',
                export_error: 'CSV export failed.',
                email_success: 'Responses sent to the campaign CSV recipient.',
                email_error: 'Emailing responses failed.',
                recipient_missing: 'No CSV recipient configured.',
                responses_error: 'Could not load campaign responses.',
                manual_export: 'Manual',
            },
            announcement: {
                name: 'Announcement |||| Announcements',
                fields: {
                    name: 'Name',
                    'public.message': 'Text',
                    'public.read_more': 'Read more link (format: https://)',
                    valid_from: 'From',
                    valid_until: 'Until',
                    valid_season: 'Valid period',
                    status: 'Status',
                    'public.applications': 'Services',
                    placements: 'Placement',
                    'public.languages': 'Language',
                    'public.categories': 'Series',
                    'public.types': 'School types',
                    'public.tags':
                        'ISBN targeting (comma-separated list of ISBNs)',
                    'public.typegroups': 'School type groups',
                    timestamp: 'Modified',
                    'public.announcement_type': 'Announcement type',
                    created: 'Created',
                    modified: 'Modified',
                    creator_name: 'Creator',
                    modifier_name: 'Modifier',
                    author: {
                        creator: 'Creator',
                        created: 'Created',
                        modifier: 'Modifier',
                        modified: 'Modified',
                    },
                },
                filters: {
                    status: 'Status',
                    status_close: 'Status',
                    languages: 'Language',
                    languages_close: 'Language',
                    applications: 'Applications',
                    applications_close: 'Applications',
                    announcement_types: 'Announcement type',
                    announcement_types_close: 'Announcement type',
                    online_status: 'Online status',
                    online_status_close: 'Online status',
                },
                tabs: {
                    details: 'Basic information',
                },
                notification: {
                    approved_success: 'Addition successful',
                    approved_error: 'Error: Addition failed',
                    rejected_success: 'Deletion successful',
                    rejected_error: 'Error: Deletion failed',
                },
                status: {
                    0: 'Public',
                    1: 'Deleted',
                    2: 'Draft',
                },
                language: {
                    fin: 'Finnish',
                    swe: 'Swedish',
                    eng: 'English',
                },
                application: {
                    oppimisenpalvelut: 'oppimisenpalvelut.otava.fi',
                    opepalvelu: 'opepalvelu.otava.fi',
                    opepalvelubeta: 'opepalvelu-beta.otava.fi',
                    oppilas: 'oppilas.otava.fi',
                    opiskelija: 'opiskelija.otava.fi',
                    finnlectura: 'finnlectura',
                    finnlectura_opiskelija: 'opiskelija.finnlectura.fi',
                    finnlectura_opettaja: 'opettaja.finnlectura.fi',
                },
                placement: {
                    1: 'Top banner',
                    2: 'Lower top banner',
                    3: 'With series',
                    4: 'News with series',
                    5: 'Message box',
                },
            },
            application: {
                label: 'Palvelu',
                name: 'Palvelu |||| Palvelut',
                actions: {
                    remove: {
                        title: 'Huom !',
                        ask: 'Poista käyttäjä palvelusta %{name}',
                        description:
                            'Poistetaanko käyttäjä palvelusta %{name} ?',
                    },
                },
                tabs: {
                    details: 'Perustiedot',
                },
                fields: {
                    last_login: 'Kirjautunut',
                    registered: 'Rekisteröitynyt',
                    name: 'Nimi',
                    username: 'Sähköposti',
                    alternative_isbn_in_use: 'Korvaava ISBN käytössä',
                    alternative_exclude_materialstorage:
                        'Älä käytä näissä materiaalivarastoissa',
                    alternative_exclude: 'Älä käytä näillä ISBN:llä',
                    alternative_isbn_text_fi: 'Suomenkielinen teksti',
                    alternative_isbn_text_sv: 'Ruotsinkielinen teksti',
                },
                status: {
                    0: 'aktiivinen',
                    1: 'Poistettu',
                },
                role: {
                    400: 'Superadmin',
                    300: 'Pääkäyttäjä',
                    250: 'Tuotepäivittäjä',
                },
            },
            valkey: {
                name: 'Valkey |||| Valkey',
                fields: {
                    key: 'Key',
                    value: 'Value',
                    ttl: 'TTL (s)',
                    size: 'Size',
                    type: 'Storage type',
                },
                filters: {
                    pattern: 'Search keys (wildcard: *)',
                },
                actions: {
                    delete_confirm:
                        'Are you sure you want to delete key %{key}?',
                },
            },
            secrets: {
                name: 'Secrets Manager |||| Secrets Manager',
                fields: {
                    name: 'Name',
                    description: 'Description',
                    secretString: 'Value',
                    createdDate: 'Created',
                    lastChangedDate: 'Last Changed',
                    lastAccessedDate: 'Last Accessed',
                    deletionDate: 'Deletion Date',
                    tags: 'Tags',
                    versionId: 'Version',
                },
                helpers: {
                    name: 'Name must start with prefix infra2/',
                },
            },
            logevent: {
                s: {
                    filter: {
                        date_from: 'Mistä',
                        date_to: 'Mihin',
                    },
                    fields: {
                        date: 'Ajankohta',
                        author: 'Käyttäjä',
                        title: 'Lisenssimäärä',
                        subtitle: 'Lisenssimäärän muutos',
                        description: 'Viesti',
                    },
                },
                p: {
                    filter: {
                        date_from: 'Mistä',
                        date_to: 'Mihin',
                    },
                    fields: {
                        date: 'Ajankohta',
                        author: 'Käyttäjä',
                        title: '',
                        subtitle: 'Private',
                        description: 'Public',
                    },
                },
            },
            locks: {
                overlay: 'Currently Edited by %{name}',
            },
        },
        login: {
            otava_ad: 'Otava login',
            otava_ad_success: 'Continue to service',
            otava_ad_failure: 'Continue to login page',
            nonce_mismatch:
                'Your account is logged in on another browser. Please log in again.',
            no_token: 'Log in to continue',
        },
        statistics: {
            title: 'Statistics |||| Statistics',
            nodata: 'No data for the selected period',
            noaccess: 'No access',
            modes: {
                single: 'Reading',
                list: 'Table',
                graph: 'Chart',
            },
            fields: {
                teachers: 'Teachers',
                pupils: 'Pupils',
                students: 'Students',
                all: 'All',
                from: 'From',
                timestamp: 'Timestamp',
                level: 'Level',
                product_guid: 'Product',
                message: 'Event',
                subscription_guid: 'Subscription',
                school_guid: 'School',
                group_guid: 'Group',
                user_agent: 'User agent',
                tags: 'Tags',
                ip: 'IP',
                show_more: 'Show more',
            },
            periods: {
                day: 'Day',
                week: 'Week',
                month: 'Month',
                year: 'Year',
                startperiod: 'Start of period (1.8.)',
                days30: 'Last 30 days',
                days90: 'Last 90 days',
            },
            activesessions: {
                title: 'Active sessions',
                tooltip: 'User has an active session in the service',
            },
            activeusers: {
                title: 'Active users',
                tooltip: 'Active in the service during the last %{day} days',
            },
            activeusershistory: {
                title: 'Active users – history',
                tooltip: 'Active users trend by day/month',
                show_page: 'Show history',
                daily: 'Daily',
                monthly: 'Monthly',
                schoolyear: 'School year',
                showtotal: 'Show total',
                chart: 'Chart',
                table: 'Table',
                export: 'Export',
                date: 'Date',
                range: 'Range',
                custom: 'Custom',
                from: 'From',
                to: 'To',
                pickrange: 'Select a valid date range.',
                holidays: 'Show holidays',
            },
            stale: {
                label: 'Data from %{time}',
                tooltip:
                    'The statistics service did not respond in time, showing the latest saved result.',
            },
            dataconfirmations: {
                title: 'Data confirmations',
                current: 'Current data status',
                own: 'Personal data',
                school: 'School data',
                own_current: 'Self-confirmed personal data, up to date',
                requires_check: 'Personal data requires checking',
                school_requires_check:
                    'Teachers with a school membership requiring checking',
                school_current: 'Self-confirmed school data, up to date',
                role: 'Role',
                roles: {
                    teacher: 'Teachers',
                    student: 'Students',
                    pupil: 'Pupils',
                },
                all_roles: 'All roles',
                group_by: 'Group by',
                day: 'Day',
                month: 'Month',
                last_login_from: 'Last login on or after',
                all_active: 'All active users',
                open: 'Show confirmation distribution',
                distribution: 'Latest confirmation distribution',
                period_totals:
                    'Latest confirmation within the selected period / selected user population',
                widget_scope:
                    'Active users who logged in during the last 12 months. Recheck threshold: %{days} days. Self-confirmed and requires-checking counts are not complements.',
                distribution_note:
                    'This is not an event history: each user is counted per role only on the day or month of their latest confirmation. A new confirmation removes them from the earlier period. Personal and school counts are separate and cannot be added to obtain a unique-user total.',
                current_note:
                    'Self-confirmed: confirmed within %{days} days, without a forced recheck. Requires checking: uses profile status, including creation date, self-modification and forced rechecks. These counts are not complements. The distribution date range does not change current status.',
                school_note:
                    'School confirmation applies to teachers. One confirmed active school membership is sufficient to be up to date. Requires-checking counts teachers with at least one active membership requiring a check, so a teacher may appear in both counts. The denominator is teachers with active school memberships; the distribution uses the latest confirmation across active memberships.',
            },
            loginstats: {
                title: 'Logins',
                tooltip: 'Login attempts during the last %{day} days',
                success: 'Successful',
                failed: 'Failed',
                nofailed: 'No failed logins',
                failure_details: 'Failure reasons',
                codes: {
                    WRONG_PASSWORD: 'Wrong password',
                    USER_NOT_FOUND: 'User not found',
                    PUPIL_NOT_FOUND: 'Pupil not found',
                    APP_NOT_FOUND: 'Application not found',
                    API_KEY_NOT_FOUND: 'API key not found',
                    TOO_MANY_LOGIN_ATTEMPTS:
                        'Account locked (too many attempts)',
                    USER_DELETED: 'User deleted',
                    USER_NOT_APPROVED: 'User not approved',
                    USER_NOT_ACTIVATED: 'User not activated',
                    USER_LOGIN_NOT_ACTIVED: 'Account not active',
                    USER_PROFILE_LOCKED: 'Profile locked',
                    TURNSTILE_VALIDATION_FAILED: 'Turnstile validation failed',
                    TURNSTILE_TOKEN_MISSING: 'Turnstile token missing',
                    TURNSTILE_VERIFY_UNREACHABLE: 'Turnstile unreachable',
                    HASH_NOT_MATCH: 'Invalid hash',
                    LOGIN_FAILED: 'Login failed',
                    DEVELOPER_TOKEN_ERROR: 'Developer token error',
                    BASIC_LOGIN_DISABLED: 'Basic login disabled',
                    INVALID_CLIENT_ID: 'Invalid client_id',
                    CLIENT_API_KEY_NOT_FOUND: 'API key not found (client)',
                    CLIENT_ENV_MISMATCH: 'Environment mismatch (client)',
                    MPASS_START_ERROR: 'MPASS login start failed',
                    MPASS_ERROR: 'MPASS session expired or invalid',
                    MPASS_NOT_VALID_ROLE:
                        "MPASS: the user's MPASS role information does not match the selected app, and the correct app could not be identified automatically.",
                    MPASS_NOT_VALID_SCHOOL_TYPE:
                        'MPASS: school type does not match the selected app (e.g. pupil in upper secondary or student in basic education)',
                    MPASS_SCHOOL_NOT_FOUND:
                        "MPASS: user's school is not recognized (school code does not match Opintopolku data)",
                    TOKEN_WRITE_ERROR: 'Login token not found',
                    JSON_TOKEN_FORBIDDEN: 'JSON token requires admin rights',
                    USER_ENVIRONMENT_RESTRICTED:
                        'Not allowed in this environment',
                    OIDC_ERROR: 'OIDC error',
                    GENERIC: 'Generic error',
                    UNKNOWN: 'Other error',
                },
            },
            newusers: {
                title: 'New users',
                tooltip: 'Registered during the last %{day} days',
            },
            newusershistory: {
                title: 'New users – history',
                tooltip: 'New users trend by day/month',
                show_page: 'Show history',
                daily: 'Daily',
                monthly: 'Monthly',
                schoolyear: 'School year',
                chart: 'Chart',
                table: 'Table',
                export: 'Export',
                date: 'Date',
                range: 'Range',
                custom: 'Custom',
                from: 'From',
                to: 'To',
                pickrange: 'Select a valid date range.',
                holidays: 'Show holidays',
            },
            producttransitions: {
                title: 'Product transitions',
                tooltip: 'TOP %{limit} product transitions during %{day} days',
                tooltip_limit: 'TOP %{limit} product transitions',
                tooltip_single: 'Product transitions during %{day} days',
            },
            userevents: {
                title: 'User events',
                tooltip: 'TOP %{limit} events during %{day} days',
                tooltip_single: 'User events during %{day} days',
            },
        },
        merge: {
            workspace: {
                help: 'Help',
                help_auto_open: 'Open help automatically',
                help_overview: 'Workflow and license indicators.',
                help_export: 'Export a review proposal for the school.',
                help_merge: 'Verify approved accounts, retained account and login.',
                help_report: 'Export a report of completed merges for the school.',
                help_history: 'Review completed merges and cancel if necessary.',
                license_legend_title: 'License indicators',
                license_legend_valid: 'License currently valid',
                license_legend_not_valid: 'Active license (not currently valid)',
                license_legend_extended: 'Extended license (earlier versions)',
                license_legend_overlapping_sample: 'School license',
                license_legend_overlapping: 'Filled indicator = school overlapping license',
                title: 'Duplicate account management',
                role: 'User role',
                export: 'Export duplicates to Excel',
                merge: 'Merging',
                report: 'Report',
                history: 'Merge history',
            },
            title: 'Merging',
            nodata: 'No accounts to merge found',
            error: 'Merging failed',
            inactive: 'User is not active, account cannot be merged',
            success: 'Merging succeeded',
            button: 'Merge',
            cancel: 'Cancel',
            continue: 'Merge anyway',
            change_profile: 'Change to primary account',
            search: {
                title: 'Search for user to merge',
                description:
                    'Search for a user to merge with the primary account. The search term can be, for example, an email address or a name.',
                must_be_same_school: 'User must be from the same school.',
                button: 'Search',
            },
            from: {
                title: 'Account to merge',
            },
            to: {
                title: 'Primary account',
            },
            conflict: {
                title: 'Detected conflicts',
                message:
                    'Ensure that the accounts being merged belong to the same user. The following information is conflicting.',
                last_login: 'Last login',
                firstname: 'First name does not match',
                lastname: 'Last name does not match',
                mpass: 'Both have MPASS connection',
                school: 'School information does not match',
                restricted_material:
                    "Note when merging teacher accounts: the primary account's digital exams are kept, but the account being merged's digital exams (Otavan Koetyökalu / Cloubi 2 digikokeet) are lost and will not transfer to the primary account",
            },
            profilepage: {
                title: 'Linked accounts',
                description: 'The following accounts are linked to the user.',
                license_currently_valid: 'License currently valid',
                license_not_currently_valid:
                    'Active license is not currently valid',
                license_overlapping:
                    'The same active school license is on another account',
                cancel_button: 'Cancel merge',
                cancel_confirm_title: 'Cancel this merge?',
                cancel_confirm_message:
                    "The merged account's data (email/username, groups and licenses) will be restored as a separate account. Both accounts' sessions will be terminated and re-login required. This action cannot be undone.",
                cancel_deadline_passed:
                    'The cancellation window has passed, this merge can no longer be cancelled.',
                cancel_materials_merged:
                    'Materials have already been merged, cancellation is no longer possible.',
                cancel_success: 'Merge cancelled',
                cancel_error: 'Failed to cancel the merge',
            },
            history: {
                mpass_status: 'MPASS profile',
                report_not_recorded: 'Not recorded',
                report_estimate_help: 'Values marked with an asterisk use the product-overlap calculation and license lists saved at merge time, not a measured change in license consumption. Recorded actual releases always take precedence.',
                report_missing_audit:
                    'Older merges may lack released license counts (“Not recorded”), which cannot be inferred retroactively.',
                mpass_moved: 'Transferred',
                mpass_merged: 'Merged',
                mpass_retained: 'Retained',
                mpass_none: 'No profile',
                mpass_not_transferred: 'Not transferred',
                mpass_unknown: 'Unknown',
                mpass_help: 'MPASS status at merge time.',
                mpass_cancelled: 'Before cancellation',
                report_title: 'School merge report',
                report_help:
                    'The report includes uncancelled merges for the school, either from the latest merge date or a selected date range.',
                report_scope: 'Report scope',
                report_latest: 'Latest merge batch',
                report_dates: 'Date range',
                report_from: 'Start date',
                report_until: 'End date',
                report_role: 'User role',
                report_export: 'Export to Excel',
                report_primary_name: 'Retained account name',
                report_primary_identifier: 'Retained account previous login',
                report_source_name: 'Merged account name',
                report_source_identifier: 'Merged account previous login',
                report_identifier_after: 'Login after merging',
                report_released_licenses: 'Returned licenses (quantity)',
                report_released_products: 'Products of returned licenses',
                report_show_products: 'Show products',
                report_unknown: 'Unknown',
                report_note:
                    'Data describes the merge operation. Contains personal data: deliver securely to the relevant school only.',
                report_invalid_dates: 'Enter valid start and end dates in the correct order.',
                report_empty: 'No non-cancelled merges were found in this selection.',
                report_error: 'Failed to export the merge report.',
                select_page: 'Select cancellable merges on this page',
                select_entry: 'Select merge: %{identifier}',
                cancel_selected: 'Cancel selected (%{count})',
                cancel_confirm: 'Cancel %{count} selected merges, newest first. Processing stops on the first error.',
                cancel_progress: 'Cancelled %{completed} / %{total}',
                cancel_result: 'Cancelled %{completed} / %{total} merges.',
                cancel_stopped: 'Processing stopped. No further merges were cancelled. Error:',
                open: 'Merge history',
                search: 'Search by name or identifier',
                description:
                    'Completed merges for the school, newest first. You can cancel a merge if cancellation conditions are met.',
                created: 'Merged',
                primary: 'Primary account',
                source: 'Merged account',
                status: 'Status',
                cancellable: 'Can be cancelled',
                cancellable_until: 'Until %{date}',
                empty: 'No merges found for this school.',
                load_error: 'Failed to load merge history.',
            },
            bulk: {
                report_export: '1. Export duplicates to Excel',
                report_completed: '3. School report',
                report_workflow:
                    '1. Export the review proposal to Excel and request school approval.\n2. Select approved accounts, check the preview and merge.\n3. Export a report of completed merges for the school.',
                report_workflow_note:
                    'Schools approve merges outside this system. Completed merges can be reviewed and cancelled in the history.',
                report_title: 'Duplicate account review list',
                report_search: 'List search filter',
                report_note:
                    'Review proposal for the school, not completed merges. Exports all matching groups. Contains personal data: deliver securely to the relevant school only.',
                report_group: 'Group',
                report_name: 'Name',
                report_active_licenses: 'This school’s active licenses',
                report_valid_active_licenses:
                    'This school’s currently valid active licenses',
                report_overlapping_licenses: 'This school’s overlapping active license rows in group',
                report_primary: 'Proposed primary account',
                report_identifier: 'Login to retain',
                report_status: 'Note',
                report_review: 'Requires separate investigation',
                report_proposal: 'Review proposal',
                report_processed: 'This group has already been processed. Refresh the duplicate list before a new review.',
                source_account: 'Account to merge',
                merge_account: 'Open the merge tab for account %{account} (opens in a new tab)',
                search: 'Search by name, email or username',
                search_help:
                    'Search by name, email or username. A group is shown if any of its accounts match.',
                filters_help:
                    'The login filter shows groups where at least one account logged in since August 1. When both filters are enabled, a group must match both conditions. Changing a filter clears selections.',
                no_search_results: 'No duplicate groups match your search.',
                logged_in_this_season: 'Logins this season',
                overlapping_licenses_only: 'Only groups with overlapping active license rows',
                open: 'Merge duplicates',
                title: 'Merge possible duplicate accounts',
                subtitle: 'School: %{school}',
                back: 'Back to list',
                invalid_context: 'Select a school and possible duplicates first.',
                no_permission: 'You do not have permission to merge users.',
                load_error: 'Failed to load duplicate accounts.',
                multiple_applications:
                    'This group contains an account linked to multiple applications and cannot be merged.',
                multiple_mpass:
                    'At least two accounts in this group have MPASS. They may be different people with the same full name, so this group is read-only and cannot be bulk merged. Review and handle the accounts individually from the user profile.',
                group_count: '%{count} duplicate groups found',
                group_count_header: 'Groups',
                accounts_count: '%{count} accounts',
                overlapping_licenses: 'Overlapping active license rows: %{count}',
                overlapping_groups: 'Overlapping groups: %{count}',
                overlapping_license: 'Overlapping active license row',
                overlapping_group: 'Overlapping group',
                groups: 'Groups',
                account_details_help:
                    'Account details show active licenses and groups (excluding archived). Overlapping school licenses are highlighted in yellow and shared groups in blue.',
                sort: 'Sort groups',
                sort_last_login_help: 'Shows groups with the most recently used account first, based on the latest login in each group. Excel uses the same order.',
                sort_licenses_help:
                    'Shows groups with the highest number of valid licenses first. Also updates the suggested account to retain (tiebreaker: most recent login).',
                sort_registered_help: 'Shows groups with the newest account first, based on the latest registration in each group. Excel uses the same order.',
                sort_merge_help:
                    'Sort duplicate groups for review: Last login (most recent first), Licenses (most valid first), or Registration (newest first). Sorting by licenses also updates the retained account proposal.',
                sort_merge_reminder:
                    'Merge only school-approved accounts. Verify the retained account and login identifier before preview and merge.',
                sort_export_help:
                    'Sort order applies to Excel: Last login (most recent first), Licenses (most valid first), or Registration (newest first). Sorting by licenses also updates the retained account proposal.',
                sort_last_login: 'Last login',
                sort_licenses: 'Licenses',
                sort_registered: 'Registration',
                select_page: 'Select / clear current page',
                no_groups: 'No duplicate groups found for the selected school.',
                primary: 'Primary account',
                identifier: 'Identifier to retain',
                identifier_domain: 'Prefer email domain',
                identifier_domain_none: 'No domain selected',
                identifier_domain_help:
                    'Choosing an email domain suggests that address as the retained login for groups with one matching account.',
                account: 'Account',
                last_login: 'Last login',
                account_created: 'Account created',
                licenses: 'Active licenses (all subscribers)',
                license_owner_school: 'this school',
                license_owner_other_school: 'another school: %{school}',
                license_owner_account: 'account holder',
                license_owner_other_account: 'account holder: %{account}',
                license_owner_unknown: 'subscriber unknown',
                license_category_school: 'This school',
                license_category_school_help:
                    'The selected school is the subscriber.',
                license_category_school_release_help:
                    'Any released licenses will return to the school license pool.',
                license_category_other_school: 'Another school',
                license_category_other_school_help:
                    'Another school is the subscriber.',
                license_category_own: 'Personal',
                license_category_own_help:
                    'A license purchased by the user.',
                license_category_unknown: 'Unknown',
                license_category_unknown_help:
                    'The license subscriber could not be identified.',
                license_validity_summary:
                    '%{valid} of %{active} active licenses currently valid',
                license_currently_valid: 'License currently valid',
                license_not_currently_valid: 'License not currently valid',
                show_details: 'Show details',
                hide_details: 'Hide details',
                mpass: 'MPASS',
                license_names: 'Licenses',
                extended_license: 'Extended license (%{count} versions)',
                classes: 'Classes',
                previous: 'Previous',
                next: 'Next',
                page: 'Page %{current} / %{total}',
                preview: 'Preview selection',
                merge_selected: 'Merge selected groups (%{count})',
                preview_title: 'Merge preview',
                preview_notice:
                    'The preview does not change accounts. Review account pairs and warnings before confirming.',
                preview_error: 'Preview failed',
                merge_error: 'Merge failed',
                results_error: 'Failed to load merge results',
                batch_finished: 'Selected groups have been processed.',
                result_success: 'Merge succeeded',
                result_partial: 'Merge was partially completed',
                result_error: 'Merge failed',
                result_cancelled: 'Merge was cancelled',
                retry: 'Refresh group to retry',
            },
        },
    }
);

export default customEnglishMessages;
